import { useQuery, useMutation } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { captureError } from '@/lib/sentry';
import type {
  AdminUserLegalConsentsView,
  AdminSignatureImage,
} from '@/types/admin';

/**
 * Fetches per-user legal-consent state for the super-admin Accounts panel.
 * RPC is super-admin gated; callers should only mount this from inside the
 * staff-only AdminBar surfaces.
 */
export function useAdminUserLegalConsents(userId: string | null) {
  return useQuery<AdminUserLegalConsentsView | null>({
    queryKey: ['admin', 'user-legal-consents', userId],
    queryFn: async () => {
      if (!userId) return null;
      const { data, error } = await supabase.rpc('admin_get_user_legal_consents', {
        p_user_id: userId,
      });
      if (error) {
        captureError(error as Error, { feature: 'admin-user-legal-consents' });
        throw error;
      }
      return data as AdminUserLegalConsentsView;
    },
    enabled: !!userId,
    staleTime: 0,
    gcTime: 30_000,
  });
}

/**
 * Fetches a single signature image dataURL on demand. Used only when an
 * admin clicks "View signature" on a digital-signature row — the list RPC
 * deliberately excludes the PNG blob to keep payload small.
 */
export function useFetchSignatureImage() {
  return useMutation<AdminSignatureImage, Error, string>({
    mutationFn: async (signedId) => {
      const { data, error } = await supabase.rpc('admin_get_signature_image', {
        p_signed_id: signedId,
      });
      if (error) {
        captureError(error as Error, { feature: 'admin-signature-image' });
        throw error;
      }
      return data as AdminSignatureImage;
    },
  });
}
